import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { HETS_PHASES } from "@/data/evara";

const EASE = [0.4, 0, 0.2, 1];

/**
 * PhaseStepper — reusable connected 6-node timeline.
 *
 * Props:
 *  - variant: "full" (framework hub / phase page — labels, tagline hover) |
 *             "mini" (services page — small dots, no labels) |
 *             "compact" (framework phase footer nav)
 *  - activeIndex: number (0-5) currently focused, or -1 for none
 *  - highlightIndices: array of 1-based phase numbers to render in the accent color (services)
 *  - onSelect: (idx0) => void — click handler with 0-based idx
 *  - showTaglineOnHover: boolean (default true for full variant)
 *  - animateOnScroll: boolean (default true) — draw-in on scroll into view
 */
export default function PhaseStepper({
  variant = "full",
  activeIndex = -1,
  highlightIndices = null, // 1-based numbers; null = all treated as regular
  onSelect,
  showTaglineOnHover = true,
  animateOnScroll = true,
  className = "",
  onNodeHover,
}) {
  const prefersReducedMotion = useReducedMotion();
  const [hoverIdx, setHoverIdx] = React.useState(-1);
  const railRef = React.useRef(null);
  const [inView, setInView] = React.useState(!animateOnScroll);

  React.useEffect(() => {
    if (!animateOnScroll || prefersReducedMotion) {
      setInView(true);
      return;
    }
    const el = railRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.25, rootMargin: "-40px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [animateOnScroll, prefersReducedMotion]);

  const isHighlighted = (num) =>
    !highlightIndices ? true : highlightIndices.includes(num);

  const sizes = {
    full: { node: 44, dot: 44, label: true, gap: "gap-3" },
    mini: { node: 22, dot: 22, label: false, gap: "gap-2" },
    compact: { node: 32, dot: 32, label: true, gap: "gap-2" },
  }[variant];

  const stagger = prefersReducedMotion ? 0 : 0.08;

  return (
    <div ref={railRef} className={"relative w-full " + className} data-testid={`phase-stepper-${variant}`}>
      {/* Rail line — animate width from 0 to 100% */}
      <div className="relative">
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-[var(--evara-border)] rounded-full" />
        <motion.div
          initial={prefersReducedMotion ? { scaleX: 1 } : { scaleX: 0 }}
          animate={inView ? { scaleX: 1 } : { scaleX: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          style={{ transformOrigin: "left center" }}
          className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-[var(--evara-primary)] rounded-full opacity-70"
        />

        <ul
          className={
            "relative grid grid-cols-6 " +
            (variant === "full" ? "gap-2 md:gap-3" : "gap-1")
          }
        >
          {HETS_PHASES.map((p, i) => {
            const num = parseInt(p.num, 10);
            const highlighted = isHighlighted(num);
            const active = i === activeIndex;
            const isHovered = hoverIdx === i;

            const nodeBg = active
              ? "var(--evara-primary)"
              : highlighted
              ? "#fff"
              : "#fff";
            const nodeBorder = active
              ? "var(--evara-primary)"
              : highlighted
              ? "var(--evara-primary)"
              : "var(--evara-border-strong)";
            const nodeText = active
              ? "#fff"
              : highlighted
              ? "var(--evara-primary)"
              : "var(--evara-muted)";

            return (
              <li
                key={p.num}
                className="relative flex flex-col items-center"
                onMouseEnter={() => {
                  setHoverIdx(i);
                  onNodeHover && onNodeHover(i);
                }}
                onMouseLeave={() => {
                  setHoverIdx(-1);
                  onNodeHover && onNodeHover(-1);
                }}
              >
                <motion.button
                  type="button"
                  onClick={() => onSelect && onSelect(i)}
                  aria-label={`Phase ${p.num} — ${p.title}`}
                  aria-current={active ? "step" : undefined}
                  className="relative rounded-full flex items-center justify-center font-serif font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--evara-primary)] focus-visible:ring-offset-2"
                  style={{
                    width: sizes.node,
                    height: sizes.node,
                    background: nodeBg,
                    color: nodeText,
                    border: `2px solid ${nodeBorder}`,
                    cursor: onSelect ? "pointer" : "default",
                  }}
                  initial={
                    prefersReducedMotion
                      ? { opacity: 1, y: 0 }
                      : { opacity: 0, y: 10, scale: 0.6 }
                  }
                  animate={
                    inView
                      ? { opacity: 1, y: 0, scale: 1 }
                      : { opacity: 0, y: 10, scale: 0.6 }
                  }
                  transition={{
                    duration: 0.4,
                    ease: EASE,
                    delay: 0.25 + i * stagger,
                  }}
                  whileHover={prefersReducedMotion ? {} : { scale: 1.08 }}
                  data-testid={`phase-stepper-node-${p.num}`}
                >
                  <span
                    className={
                      variant === "full"
                        ? "text-sm"
                        : variant === "compact"
                        ? "text-xs"
                        : "text-[9px]"
                    }
                  >
                    {p.num}
                  </span>
                </motion.button>

                {sizes.label && (
                  <motion.div
                    initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 4 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 4 }}
                    transition={{ duration: 0.35, ease: EASE, delay: 0.35 + i * stagger }}
                    className={sizes.gap + " mt-3 text-center"}
                  >
                    <div
                      className="text-[10px] uppercase tracking-[0.16em] font-semibold"
                      style={{ color: active || highlighted ? "var(--evara-primary)" : "var(--evara-muted)" }}
                    >
                      Phase {p.num}
                    </div>
                    <div
                      className={
                        (variant === "full" ? "font-serif text-sm md:text-base" : "text-xs font-semibold") +
                        " mt-0.5 leading-tight"
                      }
                      style={{ color: active || highlighted ? "var(--evara-primary)" : "var(--evara-text)" }}
                    >
                      {p.title}
                    </div>

                    {/* Hover expansion — inline tagline for full variant */}
                    {variant === "full" && showTaglineOnHover && (
                      <div
                        className="mt-1.5 hidden md:block text-[11px] text-[var(--evara-text-2)] leading-snug transition-all duration-300 overflow-hidden"
                        style={{
                          maxHeight: isHovered ? 80 : 0,
                          opacity: isHovered ? 1 : 0,
                        }}
                      >
                        {p.summary}
                      </div>
                    )}
                  </motion.div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
